import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { useUsers } from '@/hooks/useUsers';
import { salesOwnerUserListFilters } from '@/constants/sales-owner-role-codes';
import { useAuthStore } from '@/store/authStore';
import {
  useOpportunity,
  useCreateOpportunity,
  useUpdateOpportunity,
} from '@/hooks/useOpportunities';
import type { OpportunityInput } from '@/services/opportunity.service';
import DocumentList from '@/components/documents/DocumentList';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { SalesTeamSelect } from '@/components/sales/SalesTeamSelect';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import {
  OPPORTUNITY_CLOSED_STAGES,
  OPPORTUNITY_OPEN_STAGES,
  canonicalOpportunityStage,
} from '@/constants/opportunity-stages';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ALL_STAGES = [...OPPORTUNITY_OPEN_STAGES, ...OPPORTUNITY_CLOSED_STAGES] as const;

const displayStageLabel = (stage: string): string => {
  const s = canonicalOpportunityStage(stage);
  if (s === 'Won') return 'Closed won';
  if (s === 'Lost') return 'Closed lost';
  return s;
};

const OpportunityFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams<{ id: string }>();
  const isCreate = location.pathname.endsWith('/new');
  const entityId = isCreate ? undefined : editId;

  const { baseCurrency, allCurrencies } = useCurrency();
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const { data: customers, isLoading: isLoadingCustomers } = useActiveCustomers();
  const { data: opp, isLoading: loadingOpp } = useOpportunity(entityId);
  const { data: users = [] } = useUsers(salesOwnerUserListFilters(isCreate ? undefined : opp?.sales_owner));
  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
    enabled: isCreate,
  });
  const createOpp = useCreateOpportunity();
  const updateOpp = useUpdateOpportunity();

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const rstOptions = useMemo(
    () => revenueServiceTypes.map((r) => ({ value: r.id, label: `${r.service_code} — ${r.service_name}` })),
    [revenueServiceTypes]
  );

  const toNumber = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  useEffect(() => {
    if (!entityId || !opp) return;
    form.setFieldsValue({
      opportunity_name: opp.opportunity_name,
      customer_name: opp.customer_name,
      customer_id: opp.customer_id,
      contact_person: opp.contact_person,
      email: opp.email,
      phone: opp.phone,
      opportunity_type: opp.opportunity_type,
      industry: opp.industry,
      project_type: opp.project_type,
      competitors: opp.competitors,
      requirements: opp.requirements,
      project_duration_months: opp.project_duration_months,
      stage: canonicalOpportunityStage(opp.stage),
      probability: opp.probability,
      estimated_value: toNumber(opp.estimated_value),
      currency: opp.currency || baseCurrency?.currency_code,
      expected_close_date: opp.expected_close_date ? dayjs(opp.expected_close_date) : undefined,
      sales_owner: opp.sales_owner,
      notes: opp.notes,
      revenue_service_type_id: opp.revenue_service_type_id || undefined,
    });
  }, [entityId, opp, form, baseCurrency?.currency_code]);

  useEffect(() => {
    if (isCreate) {
      form.resetFields();
      const defaultOwner = user?.id && users.some((u) => u.id === user.id) ? user.id : undefined;
      form.setFieldsValue({
        stage: 'Prospecting',
        status: 'Open',
        probability: 40,
        currency: baseCurrency?.currency_code || 'AED',
        sales_owner: defaultOwner,
        sales_team_id: salesTeams.length === 1 ? salesTeams[0].id : undefined,
      });
    }
  }, [isCreate, form, baseCurrency?.currency_code, user?.id, salesTeams, users]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const exp = values.expected_close_date as Dayjs | undefined;
    const selCustId = values.customer_id as string;
    const selCustomer = customers?.find((c) => c.id === selCustId);
    const customerName = selCustomer?.customer_name || (values.customer_name as string) || '';

    const payload: OpportunityInput = {
      opportunity_name: values.opportunity_name as string,
      customer_id: selCustId,
      customer_name: customerName,
      contact_person: values.contact_person as string | undefined,
      email: values.email as string | undefined,
      phone: values.phone as string | undefined,
      opportunity_type: values.opportunity_type as string | undefined,
      industry: values.industry as string | undefined,
      project_type: values.project_type as string | undefined,
      competitors: values.competitors as string | undefined,
      requirements: values.requirements as string | undefined,
      project_duration_months:
        values.project_duration_months != null ? Number(values.project_duration_months) : undefined,
      stage: values.stage as string,
      probability: values.probability != null ? Number(values.probability) : 40,
      estimated_value: values.estimated_value != null ? Number(values.estimated_value) : undefined,
      currency: values.currency as string,
      expected_close_date: exp ? exp.format('YYYY-MM-DD') : undefined,
      sales_owner: values.sales_owner as string,
      notes: values.notes as string | undefined,
      revenue_service_type_id: (values.revenue_service_type_id as string | undefined) || null,
    };

    if (isCreate) {
      payload.status = 'Open';
      payload.sales_team_id = (values.sales_team_id as string | undefined) || null;
      const created = await createOpp.mutateAsync(payload);
      navigate(`/growth/opportunities/${created.id}/edit`, { replace: true });
    } else if (entityId) {
      if (opp?.status) payload.status = opp.status;
      await updateOpp.mutateAsync({ id: entityId, data: payload });
    }
  };

  if (!isCreate && entityId && loadingOpp) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/opportunities')}>
          Back to opportunities
        </Button>
      </Space>
      <Title level={3}>{isCreate ? 'New opportunity' : 'Edit opportunity'}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        {isCreate
          ? 'Create an opportunity; you can attach documents after saving.'
          : `Opportunity ${opp?.opportunity_number ?? ''}`}
      </Text>

      <Card style={{ marginBottom: 24 }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="opportunity_name"
                label="Opportunity name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_id" label="Customer" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  loading={isLoadingCustomers}
                  optionFilterProp="label"
                  options={(customers || []).map((c) => ({
                    label: c.customer_name,
                    value: c.id,
                  }))}
                  placeholder="Select customer"
                  onChange={(custId: string) => {
                    const c = customers?.find((x) => x.id === custId);
                    form.setFieldValue('customer_name', c?.customer_name || '');
                  }}
                />
              </Form.Item>
              <Form.Item name="customer_name" hidden>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contact_person" label="Contact person">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sales_owner" label="Sales owner" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Sales executive, manager, director, or GM sales"
                  notFoundContent="No users with a sales owner role"
                  options={users.map((u) => ({
                    label: `${u.first_name} ${u.last_name || ''}`.trim(),
                    value: u.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {isCreate ? (
                <SalesTeamSelect />
              ) : (
                <Form.Item label="Sales team">
                  <Input disabled value={opp?.sales_team_name || opp?.sales_team_id || '—'} />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="revenue_service_type_id"
                label="Revenue service type"
                rules={[{ required: true, message: 'Required — used for quotations, customer orders, and invoice revenue GL' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select revenue / service type"
                  options={rstOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="opportunity_type" label="Opportunity type">
                <Select
                  allowClear
                  options={['New Business', 'Existing Customer', 'Renewal'].map((x) => ({ label: x, value: x }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="industry" label="Industry">
                <Input placeholder="e.g. Façade" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="project_type" label="Project type">
                <Input placeholder="Curtain wall, glazing..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="competitors" label="Competitors">
                <TextArea rows={2} placeholder="Competitor notes" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="requirements" label="Requirements">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="stage" label="Stage" rules={[{ required: true }]}>
                <Select options={ALL_STAGES.map((s) => ({ label: displayStageLabel(s), value: s }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="probability" label="Probability %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="project_duration_months" label="Duration (months)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="estimated_value" label="Estimated value">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="Currency" rules={[{ required: true }]}>
                <Select
                  options={allCurrencies.map((c) => ({
                    label: (() => {
                      const d = uiPlainCurrencyLabel(c.symbol, c.currency_code);
                      return d === c.currency_code
                        ? `${c.currency_code} (${c.currency_name})`
                        : `${d} ${c.currency_code}`;
                    })(),
                    value: c.currency_code,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_close_date" label="Expected close">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={createOpp.isPending || updateOpp.isPending}>
              Save
            </Button>
            <Button onClick={() => navigate('/growth/opportunities')}>Cancel</Button>
          </Space>
        </Form>
      </Card>

      {entityId ? (
        <Card title="Attachments">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <DocumentUpload entityType="Opportunity" entityId={entityId} />
            <DocumentList entityType="Opportunity" entityId={entityId} />
          </Space>
        </Card>
      ) : null}
    </div>
  );
};

export default OpportunityFormPage;
